/**
 * CentersPage - 센터 관리 페이지
 *
 * Admin이 소유한 센터 목록을 조회하고,
 * 새 센터 추가 및 기존 센터 삭제 기능을 제공한다.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useCenters, useDeleteCenter } from "@/hooks/useCenters";
import { useCenterContext } from "@/contexts/CenterContext";
import { CreateCenterModal } from "@/components/CreateCenterModal";

/** 날짜 문자열을 "YYYY.MM.DD" 형식으로 변환한다. */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
}

export function CentersPage() {
  const { data, isLoading, isError, error, refetch } = useCenters();
  const deleteCenter = useDeleteCenter();
  const { refreshCenters } = useCenterContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const centers = data?.centers ?? [];

  /** 센터 삭제 확인 및 실행 */
  const handleDelete = async (centerId: number, centerName: string) => {
    const confirmed = window.confirm(
      `"${centerName}"을(를) 삭제하시겠습니까? 모든 데이터가 영구 삭제됩니다.`,
    );
    if (!confirmed) return;

    try {
      await deleteCenter.mutateAsync({ centerId });
      await refreshCenters();
    } catch (err: unknown) {
      console.error("Failed to delete center:", err);
    }
  };

  /** 센터 생성 완료 콜백 */
  const handleCreated = async () => {
    setIsModalOpen(false);
    await refreshCenters();
  };

  // --- 로딩 상태 ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">센터 관리</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-2/5" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">센터 관리</h1>
        </div>
        <div className="bg-red-50 text-red-600 p-6 rounded-lg text-center">
          <p className="text-lg font-medium">센터 목록을 불러오지 못했습니다.</p>
          <p className="mt-1 text-sm text-red-500">
            {error instanceof Error ? error.message : "알 수 없는 오류"}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // --- 빈 상태 ---
  if (centers.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">센터 관리</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          {/* 빈 상태 아이콘 */}
          <svg
            className="w-16 h-16 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p className="text-lg font-medium text-gray-700">아직 등록된 센터가 없습니다.</p>
          <p className="mt-1 text-sm text-gray-500">센터를 추가하여 센터 관리를 시작하세요.</p>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            센터 추가
          </button>
        </div>

        <CreateCenterModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onCreated={handleCreated}
        />
      </div>
    );
  }

  // --- 정상 상태: 센터 목록 ---
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">센터 관리</h1>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          센터 추가
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {centers.map((center) => (
          <div
            key={center.centerId}
            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow flex flex-col"
          >
            <Link to={`/centers/${center.centerId}`} className="p-6 flex-1 block">
              <h3 className="text-lg font-bold text-gray-900">{center.name}</h3>

              {center.location && <p className="mt-2 text-sm text-gray-500">{center.location}</p>}

              {center.contact && <p className="mt-1 text-sm text-gray-500">{center.contact}</p>}

              <p className="mt-1 text-sm text-gray-400">{formatDate(center.createdAt)}</p>
            </Link>

            {/* 하단 삭제 버튼 영역 */}
            <div className="px-6 pb-4 flex justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete(center.centerId, center.name);
                }}
                disabled={deleteCenter.isPending}
                className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50"
              >
                {deleteCenter.isPending ? "삭제 중..." : "삭제"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <CreateCenterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
